import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { studentsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import AttestationMenu from '../components/students/AttestationMenu';
import PushToggleCard from '../components/dashboard/PushToggleCard';
import {
  Users, GraduationCap, CalendarCheck, Money, ArrowRight, EnvelopeSimple,
  MagnifyingGlass, FileText, Archive,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const SecretariatDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [q, setQ] = useState('');

  useEffect(() => {
    axios.get(`${API}/api/secretariat/dashboard`, { withCredentials: true })
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
    // Pre-fetch active + archived so search covers alumni
    studentsApi.getAll({ status: 'all' }).then(r => setStudents(r.data || [])).catch(() => {});
  }, []);

  const results = useMemo(() => {
    if (!q || q.length < 2) return [];
    const t = q.toLowerCase();
    return students.filter(s =>
      `${s.first_name} ${s.last_name}`.toLowerCase().includes(t)
      || (s.class_id || '').toLowerCase().includes(t)
    ).slice(0, 8);
  }, [q, students]);

  if (loading) return <p className="p-8 text-sm">Chargement…</p>;
  if (!data || data.error) return <p className="p-8 text-sm text-red-600">Erreur de chargement</p>;

  return (
    <div className="space-y-6" data-testid="secretariat-dashboard">
      <div className="flex items-center gap-3">
        <EnvelopeSimple size={28} className="text-[#0052FF]" weight="duotone" />
        <h1 className="font-outfit text-2xl font-bold text-[#0F172A]">
          Tableau de bord — Secrétariat
        </h1>
      </div>

      {/* Push notifications toggle */}
      <PushToggleCard />

      {/* Quick student search — the #1 front-desk action */}
      <Card className="border-2 border-[#0052FF]/20 shadow-none">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 text-[#0052FF]">
            <MagnifyingGlass size={18} weight="duotone" />
            <span className="font-outfit font-semibold">Rechercher un élève (actif ou archivé)</span>
          </div>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Tapez un prénom, un nom ou une classe…"
            className="border-[#E2E8F0]"
            data-testid="sec-search-input"
          />
          {results.length > 0 && (
            <ul className="border border-[#E2E8F0] rounded-md divide-y" data-testid="sec-search-results">
              {results.map(s => (
                <li
                  key={s.id}
                  className="flex items-center justify-between p-2 text-sm hover:bg-[#F8FAFC]"
                  data-testid={`sec-search-row-${s.id}`}
                >
                  <div>
                    <span className="font-medium">{s.last_name} {s.first_name}</span>
                    <span className="ml-2 text-xs text-[#64748B]">{s.class_id || '—'}</span>
                    {s.status && s.status !== 'active' && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
                        {s.status === 'graduated' ? '🎓 Diplômé' : '📦 ' + (s.archive_reason || 'archivé')}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/students/${s.id}/cursus`}
                      className="text-xs text-[#0052FF] hover:underline"
                    >
                      <FileText size={14} className="inline mr-0.5" /> Cursus
                    </Link>
                    <AttestationMenu student={s} />
                  </div>
                </li>
              ))}
            </ul>
          )}
          {q.length >= 2 && results.length === 0 && (
            <p className="text-xs text-[#94A3B8]">Aucun élève trouvé.</p>
          )}
        </CardContent>
      </Card>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Link to="/students">
          <Card className="border border-[#E2E8F0] shadow-none hover:border-[#0052FF] transition-colors cursor-pointer">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-[#64748B]">
                <Users size={18} /><span className="text-xs uppercase">Élèves actifs</span>
              </div>
              <p className="font-outfit text-2xl font-bold mt-1" data-testid="sec-kpi-students">
                {data.active_students}
              </p>
            </CardContent>
          </Card>
        </Link>
        <Card className="border border-[#E2E8F0] shadow-none">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-[#64748B]">
              <GraduationCap size={18} /><span className="text-xs uppercase">Inscriptions ce mois</span>
            </div>
            <p className="font-outfit text-2xl font-bold mt-1 text-[#0052FF]" data-testid="sec-kpi-enrolments">
              {data.enrolments_this_month}
            </p>
          </CardContent>
        </Card>
        <Link to="/appointments">
          <Card className="border border-[#E2E8F0] shadow-none hover:border-[#0052FF] transition-colors cursor-pointer">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-[#64748B]">
                <CalendarCheck size={18} /><span className="text-xs uppercase">RDV en attente</span>
              </div>
              <p
                className={`font-outfit text-2xl font-bold mt-1 ${
                  data.pending_appointments > 0 ? 'text-amber-600' : ''
                }`}
                data-testid="sec-kpi-appts"
              >
                {data.pending_appointments}
              </p>
            </CardContent>
          </Card>
        </Link>
        <Link to="/payments">
          <Card className="border border-[#E2E8F0] shadow-none hover:border-[#0052FF] transition-colors cursor-pointer">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-[#64748B]">
                <Money size={18} /><span className="text-xs uppercase">Paiements aujourd&apos;hui</span>
              </div>
              <p className="font-outfit text-2xl font-bold mt-1 text-green-600" data-testid="sec-kpi-payments">
                {data.today_payments_count}
              </p>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Recent lists */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border border-[#E2E8F0] shadow-none">
          <CardHeader>
            <CardTitle className="font-outfit text-base flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Users size={18} className="text-[#0052FF]" /> Dernières inscriptions
              </span>
              <Link to="/students" className="text-xs text-[#0052FF] hover:underline flex items-center gap-1">
                Tout voir <ArrowRight size={12} />
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.recent_students.length === 0
              ? <p className="text-sm text-[#94A3B8]">Aucune inscription récente</p>
              : (
                <ul className="space-y-2 text-sm">
                  {data.recent_students.map(s => (
                    <li key={s.id}
                      className="flex justify-between border-b border-[#F1F5F9] pb-1 last:border-0"
                      data-testid={`sec-student-${s.id}`}
                    >
                      <span>{s.name} <span className="text-xs text-[#64748B]">{s.class_name}</span></span>
                      <span className="text-xs text-[#94A3B8]">{(s.created_at || '').slice(0, 10)}</span>
                    </li>
                  ))}
                </ul>
              )}
          </CardContent>
        </Card>

        <Card className="border border-[#E2E8F0] shadow-none">
          <CardHeader>
            <CardTitle className="font-outfit text-base flex items-center justify-between">
              <span className="flex items-center gap-2">
                <CalendarCheck size={18} className="text-amber-600" /> Prochains RDV
              </span>
              <Link to="/appointments" className="text-xs text-[#0052FF] hover:underline flex items-center gap-1">
                Tout voir <ArrowRight size={12} />
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.upcoming_appointments.length === 0
              ? <p className="text-sm text-[#94A3B8]">Aucun RDV à venir</p>
              : (
                <ul className="space-y-2 text-sm">
                  {data.upcoming_appointments.map(a => (
                    <li key={a.id}
                      className="flex justify-between border-b border-[#F1F5F9] pb-1 last:border-0"
                      data-testid={`sec-appt-${a.id}`}
                    >
                      <span>{a.parent_name} → {a.teacher_name}</span>
                      <span className="text-xs text-[#94A3B8]">{a.date} {a.time}</span>
                    </li>
                  ))}
                </ul>
              )}
          </CardContent>
        </Card>
      </div>

      {/* Quick links row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Link to="/appointments" className="p-3 border border-[#E2E8F0] rounded-md hover:border-[#0052FF] flex items-center gap-2" data-testid="sec-quick-appointments">
          <CalendarCheck size={18} className="text-[#0052FF]" weight="duotone" />
          <span className="text-sm">Créer un RDV</span>
        </Link>
        <Link to="/payments" className="p-3 border border-[#E2E8F0] rounded-md hover:border-[#0052FF] flex items-center gap-2" data-testid="sec-quick-payments">
          <Money size={18} className="text-emerald-600" weight="duotone" />
          <span className="text-sm">Recevoir un paiement</span>
        </Link>
        <Link to="/students" className="p-3 border border-[#E2E8F0] rounded-md hover:border-[#0052FF] flex items-center gap-2" data-testid="sec-quick-enrolment">
          <GraduationCap size={18} className="text-[#0052FF]" weight="duotone" />
          <span className="text-sm">Nouvelle inscription</span>
        </Link>
        <Link to="/archived-students" className="p-3 border border-[#E2E8F0] rounded-md hover:border-[#0052FF] flex items-center gap-2" data-testid="sec-quick-archives">
          <Archive size={18} className="text-slate-600" weight="duotone" />
          <span className="text-sm">Archives élèves</span>
        </Link>
      </div>
    </div>
  );
};

export default SecretariatDashboard;
